import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from 'app/AppStore';
import { DataContainer } from 'components/DataContainer';
import Loader from 'components/Loader';
import { getExplodedTree, getLastSearchedVehicleKey } from 'domains/catalog/Catalog.store';
import {
  getEasyBasketSelectedOperationId,
  getEasyBasketOperationIdsSortedFiltered,
  getEasyBasketOperationsByIds,
} from 'domains/easyBasket/EasyBasket.store';
import OperationSelector from 'pages/CatalogPage/DH/SubcategorySection/OperationsSection/OperationSelection/OperationSelector';
import { LoadingWrapper } from 'pages/CatalogPage/DH/SubcategorySection/OperationsSection/OperationsSection.styled';
import { Flex, Text } from 'UI';
import { isLoading, LOADING, SEARCH_STATUS } from 'utils';

export interface SelectOperationProps {
  operationsIds: string[];
  categoryLabel: string;
  onSelectOperation: (operationId: string) => void;
  selectedOperationSearchStatus: SEARCH_STATUS;
}

const SelectOperation = ({
  operationsIds,
  categoryLabel,
  onSelectOperation,
  selectedOperationSearchStatus,
}: SelectOperationProps) => {
  const { t } = useTranslation();
  const vehicleKey = useSelector(getLastSearchedVehicleKey);
  const catalogTree = useSelector((state: RootState) => getExplodedTree(state, vehicleKey));
  const selectedOperationId = useSelector((state: RootState) => getEasyBasketSelectedOperationId(state));
  const easyBasketOperations = useSelector((state: RootState) => getEasyBasketOperationsByIds(state, operationsIds));
  const operationsIdsSortedFiltered = useSelector((state: RootState) =>
    getEasyBasketOperationIdsSortedFiltered(state, operationsIds),
  );
  const isAnyOperationLoading = Array.from(easyBasketOperations.values()).some((operation) =>
    isLoading(operation?.searchStatus),
  );
  const getStatus = () => (isAnyOperationLoading ? LOADING : catalogTree?.searchStatus);

  return (
    <Flex align={'flex-start'} gap={15}>
      <Flex direction={'column'} size={0} minWidth={512} maxWidth={512} gap={25} padding={'20px 0'}>
        <Flex direction={'column'} gap={15}>
          <Text type="heading4" disableGutter>
            {categoryLabel}
          </Text>
          <Text type="base">
            {t(
              'catalog.easy_basket.tab.select_operation.description',
              'Select operation available for {{operationLabel}} operation to access our recommendations.',
              { operationLabel: categoryLabel },
            )}
          </Text>
        </Flex>
        <DataContainer data={getStatus()}>
          <Flex direction={'column'}>
            {operationsIdsSortedFiltered.map((operationId) => (
              <OperationSelector
                key={operationId}
                operationId={operationId}
                selectedOperationId={selectedOperationId}
                onSelectOperation={onSelectOperation}
              />
            ))}
          </Flex>
        </DataContainer>
      </Flex>
      <LoadingWrapper justify={'center'} minHeight={512} maxHeight={512} align={'center'}>
        <DataContainer data={selectedOperationSearchStatus} Loading={() => <Loader size={'default'} withLabel />} />
      </LoadingWrapper>
    </Flex>
  );
};

export default SelectOperation;
